/**
 * Q-01 -- list-page filters that survive leaving the module and coming back.
 *
 * `useFilterParams` (and `ReportsPage`) already mirror filters into the URL,
 * which covers a refresh. It does not cover Policies -> Quotes -> Policies:
 * the sidebar links go to BARE paths, so the return trip lands on an empty
 * query string. This module is the other half: the last filter query string
 * each page wrote, remembered for the browser session.
 *
 * Deliberate choices:
 *
 * - `sessionStorage`, not `localStorage`: "for the session" is the request,
 *   and a closed tab should not come back filtered days later.
 * - KEYED BY USER ID + PAGE. Two people sharing a browser (a branch counter)
 *   must never inherit each other's filters; and `forgetAllRememberedFilters`
 *   runs on logout besides.
 * - EVERY storage access is wrapped. Private windows, blocked site data and
 *   quota errors throw on access; a throw here must cost the user the
 *   convenience, never the page.
 * - What is stored is the page's OWN encoded query string (the `f_` params, or
 *   Reports' filter params), so a restore goes back through the same decoder
 *   a URL does -- a stale or hand-edited entry is validated exactly as a stale
 *   link would be.
 */

const KEY_PREFIX = 'policy.filters.v1:';

function storageKey(userId: string, page: string): string {
  return `${KEY_PREFIX}${userId}:${page}`;
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** The remembered query string for this user's page, or `null` for none. */
export function readRememberedFilters(userId: string | null | undefined, page: string): string | null {
  if (!userId) return null;
  try {
    const value = storage()?.getItem(storageKey(userId, page)) ?? null;
    return value ? value : null;
  } catch {
    return null;
  }
}

/**
 * Remember `query` for this user's page. An EMPTY query forgets the page --
 * that is what makes "Clear filters" stick across the away-and-back trip.
 */
export function writeRememberedFilters(
  userId: string | null | undefined,
  page: string,
  query: string
): void {
  if (!userId) return;
  try {
    const store = storage();
    if (!store) return;
    if (query) store.setItem(storageKey(userId, page), query);
    else store.removeItem(storageKey(userId, page));
  } catch {
    // Storage unavailable or full: the page still works, it just forgets.
  }
}

/** Forget every remembered filter, for every user and page. Run on logout. */
export function forgetAllRememberedFilters(): void {
  try {
    const store = storage();
    if (!store) return;
    const doomed: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key && key.startsWith(KEY_PREFIX)) doomed.push(key);
    }
    for (const key of doomed) store.removeItem(key);
  } catch {
    // Nothing to do: a storage we cannot read holds nothing we can clear.
  }
}

/**
 * True when the URL carries NO query params at all -- the only case in which
 * a remembered state may be restored. Any param (a prefixed filter, an
 * unprefixed deep-link seed such as `?insurer=`, `?contact_type=`, `?tab=`)
 * means someone asked for something specific, and that always wins.
 */
export function isBareQuery(params: URLSearchParams): boolean {
  return params.toString() === '';
}
